import React, { useState, useEffect, useRef, Fragment } from 'react';
import toast from 'react-hot-toast';
import { insumosService } from '../api/insumos';
import { Menu, Transition } from '@headlessui/react';
import { FunnelIcon, PrinterIcon, PlusIcon } from '@heroicons/react/24/outline';
import ResponsiveTable from '../components/common/ResponsiveTable';
import AddButton from '../components/ui/AddButton';

const InventarioPage = () => {
  const [insumos, setInsumos] = useState([]);
  const [insumosFiltered, setInsumosFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingInsumo, setEditingInsumo] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [formError, setFormError] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    soloStockCritico: false,
    soloProximosVencer: false,
    soloVencidos: false,
    ordenarPor: 'nombre',
    ordenDireccion: 'asc'
  });

  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    unidad_medida: '',
    stock_actual: '',
    stock_critico: '',
    valor_unitario: '',
    contenido_envase: '',
    valor_aplicacion: '',
    fecha_vencimiento: ''
  });

  // Helper para capitalizar texto: respeta mayúsculas, capitaliza minúsculas
  const formatInputText = (text) => {
    if (!text) return "";
    // Si el usuario escribió todo mayúscula (ej: "BISTURI"), lo respetamos.
    const isAllCaps = text === text.toUpperCase();
    if (isAllCaps && text.length > 1) return text;
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  useEffect(() => {
    cargarInsumos();
  }, []);

  useEffect(() => {
    aplicarFiltros();
  }, [insumos, filterOptions]);

  // Efecto para cálculo automático de valor por aplicación
  useEffect(() => {
    const price = parseFloat(formData.valor_unitario);
    const content = parseFloat(formData.contenido_envase);

    if (price > 0 && content > 0) {
      const calculated = Math.round(price / content);
      setFormData(prev => ({ ...prev, valor_aplicacion: calculated.toString() }));
    }
  }, [formData.valor_unitario, formData.contenido_envase]);

  const cargarInsumos = async () => {
    try {
      const response = await insumosService.getAll();
      const data = Array.isArray(response.data) ? response.data : [];
      setInsumos(data);
      
      // Verificar stock crítico
      const insumosCriticos = data.filter(
        insumo => insumo.stock_actual <= insumo.stock_critico
      );
      
      if (insumosCriticos.length > 0) {
        setAlertMessage(`¡Alerta! Los siguientes insumos están en stock crítico: ${insumosCriticos.map(i => i.nombre).join(', ')}`);
        setShowAlert(true);
      }
    } catch (error) {
      console.error('Error al cargar insumos:', error);
      setInsumos([]);
    } finally {
      setLoading(false);
    }
  };

  const aplicarFiltros = () => {
    let insumosOrdenados = [...insumos];
    
    // Aplicar filtros de estado
    if (filterOptions.soloStockCritico) {
      insumosOrdenados = insumosOrdenados.filter(hasLowStock);
    }
    if (filterOptions.soloProximosVencer) {
      insumosOrdenados = insumosOrdenados.filter(isExpiringSoon);
    }
    if (filterOptions.soloVencidos) {
      insumosOrdenados = insumosOrdenados.filter(isExpired);
    }
    
    // Ordenar según el criterio seleccionado
    insumosOrdenados.sort((a, b) => {
      let comparacion = 0;
      
      if (filterOptions.ordenarPor === 'nombre') {
        comparacion = a.nombre.localeCompare(b.nombre);
      } else if (filterOptions.ordenarPor === 'stock_actual') {
        comparacion = a.stock_actual - b.stock_actual;
      } else if (filterOptions.ordenarPor === 'stock_critico') {
        comparacion = a.stock_actual - a.stock_critico - (b.stock_actual - b.stock_critico);
      }
      
      return filterOptions.ordenDireccion === 'asc' ? comparacion : -comparacion;
    });
    
    setInsumosFiltered(insumosOrdenados);
  };

  // Función para imprimir usando window.print() nativo del navegador
  const imprimirListaCompras = () => {
    // Primero aplicamos el filtro de stock crítico
    const filtroAnterior = filterOptions.soloStockCritico;
    setFilterOptions(prev => ({
      ...prev,
      soloStockCritico: true
    }));

    // Damos un poco de tiempo para que se aplique el filtro
    setTimeout(() => {
      // Creamos una ventana nueva para la impresión
      const ventanaImpresion = window.open('', '_blank');
      
      if (!ventanaImpresion) {
        toast.error("Por favor permite ventanas emergentes para imprimir.");
        return;
      }

      // Helper para verificar si un producto está vencido
      const estaVencido = (insumo) => {
        if (!insumo.fecha_vencimiento) return false;
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0); // Normalizar a inicio del día
        const fechaVencimiento = new Date(insumo.fecha_vencimiento);
        return fechaVencimiento < hoy;
      };

      // Obtenemos insumos que necesitan compra:
      // A) Stock bajo (stock <= stock_critico), O
      // B) Productos vencidos (fecha_vencimiento < hoy)
      const insumosParaCompra = insumos.filter(insumo => {
        const stockBajo = insumo.stock_actual <= insumo.stock_critico;
        const vencido = estaVencido(insumo);
        return stockBajo || vencido;
      });

      // Función para calcular el stock útil y faltante
      const calcularFaltante = (insumo) => {
        const vencido = estaVencido(insumo);
        // Si está vencido, el stock útil es 0 (no sirve para nada)
        const stockUtil = vencido ? 0 : insumo.stock_actual;
        // Faltante = Stock Crítico - Stock Útil
        const faltante = insumo.stock_critico - stockUtil;
        return faltante > 0 ? faltante : 0;
      };
      
      // Creamos el contenido HTML de la impresión
      const contenidoHTML = `
        <!DOCTYPE html>
        <html lang="es">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Lista de Insumos para Compra</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
            }
            h1 {
              text-align: center;
              margin-bottom: 10px;
            }
            .fecha {
              text-align: center;
              color: #666;
              margin-bottom: 20px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f2f2f2;
              font-weight: bold;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            tr.critico {
              background-color: #ffeeee;
            }
            tr.vencido {
              background-color: #f3f4f6;
            }
            .faltante {
              font-weight: bold;
            }
            .fecha-vencida {
              color: #dc2626;
              font-weight: bold;
            }
            .motivo-vencido {
              color: #dc2626;
              font-size: 0.75em;
              font-weight: bold;
            }
            @media print {
              button {
                display: none;
              }
            }
          </style>
        </head>
        <body>
          <h1>Lista de Insumos para Compra</h1>
          <p class="fecha">Fecha: ${new Date().toLocaleDateString()}</p>
          
          <table>
            <thead>
              <tr>
                <th>Producto</th>
                <th>Stock</th>
                <th>Crítico</th>
                <th>Unidad</th>
                <th>V. Unit.</th>
                <th>V. Aplic.</th>
                <th>Vencimiento</th>
                <th>Faltante</th>
              </tr>
            </thead>
            <tbody>
              ${insumosParaCompra.length > 0 
                ? insumosParaCompra.map(insumo => {
                    const vencido = estaVencido(insumo);
                    const faltante = calcularFaltante(insumo);
                    return `
                      <tr class="${vencido ? 'vencido' : 'critico'}">
                        <td>
                          ${insumo.nombre}
                          ${vencido ? '<span class="motivo-vencido"> [VENCIDO]</span>' : ''}
                          <br><small style="color: #666;">${insumo.descripcion || ''}</small>
                        </td>
                        <td>${vencido ? `<s>${insumo.stock_actual}</s> → 0` : insumo.stock_actual}</td>
                        <td>${insumo.stock_critico}</td>
                        <td>${insumo.unidad_medida}</td>
                        <td>${insumo.valor_unitario_formato}</td>
                        <td style="color: #059669; font-weight: bold;">${insumo.valor_aplicacion_formato}</td>
                        <td class="${vencido ? 'fecha-vencida' : ''}">${insumo.fecha_vencimiento ? new Date(insumo.fecha_vencimiento).toLocaleDateString() : '-'}</td>
                        <td class="faltante">${faltante}</td>
                      </tr>
                    `;
                  }).join('')
                : '<tr><td colspan="8" style="text-align: center;">No hay insumos para comprar</td></tr>'
              }
            </tbody>
          </table>

          <div style="text-align: center; margin-top: 20px;">
            <button onclick="window.print()">Imprimir</button>
          </div>
        </body>
        </html>
      `;
      
      // Escribimos el contenido en la nueva ventana
      ventanaImpresion.document.open();
      ventanaImpresion.document.write(contenidoHTML);
      ventanaImpresion.document.close();
      
      // Restauramos el filtro después de un tiempo
      setTimeout(() => {
        setFilterOptions(prev => ({
          ...prev,
          soloStockCritico: filtroAnterior
        }));
      }, 500);
    }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    
    try {
      // Convertir valores de string a número y aplicar formato de texto
      const insumoData = {
        ...formData,
        nombre: formatInputText(formData.nombre),
        descripcion: formatInputText(formData.descripcion),
        stock_actual: parseInt(formData.stock_actual),
        stock_critico: parseInt(formData.stock_critico),
        valor_unitario: parseInt(formData.valor_unitario),
        contenido_envase: parseInt(formData.contenido_envase) || null,
        valor_aplicacion: parseInt(formData.valor_aplicacion),
        fecha_vencimiento: formData.fecha_vencimiento || null
      };

      if (editingInsumo) {
        await insumosService.update(editingInsumo.id, insumoData);
      } else {
        await insumosService.create(insumoData);
      }
      
      setShowForm(false);
      setEditingInsumo(null);
      await cargarInsumos();
      setFormData({
        nombre: '',
        descripcion: '',
        unidad_medida: '',
        stock_actual: '',
        stock_critico: '',
        valor_unitario: '',
        contenido_envase: '',
        valor_aplicacion: '',
        fecha_vencimiento: ''
      });
      
      // Mostrar mensaje de éxito
      setAlertMessage(editingInsumo ? 'Insumo actualizado correctamente' : 'Insumo creado correctamente');
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
      
    } catch (error) {
      console.error('Error al guardar insumo:', error);
      setFormError('Error al guardar el insumo. Por favor, intente nuevamente.');
    }
  };

  const handleEdit = (insumo) => {
    setEditingInsumo(insumo);
    setFormData({
      nombre: insumo.nombre,
      descripcion: insumo.descripcion || '',
      unidad_medida: insumo.unidad_medida,
      stock_actual: insumo.stock_actual.toString(),
      stock_critico: insumo.stock_critico.toString(),
      valor_unitario: insumo.valor_unitario || '',
      contenido_envase: insumo.contenido_envase || '',
      valor_aplicacion: insumo.valor_aplicacion || '',
      fecha_vencimiento: insumo.fecha_vencimiento || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Está seguro de eliminar este insumo?')) {
      try {
        const response = await insumosService.delete(id);
        if (response.status === 204 || response.status === 200) {
          await cargarInsumos();
          setAlertMessage('Insumo eliminado correctamente');
          setShowAlert(true);
          setTimeout(() => setShowAlert(false), 3000);
        }
      } catch (error) {
        console.error('Error al eliminar insumo:', error);
        setAlertMessage('No se puede eliminar el insumo porque está siendo utilizado en alguna ficha clínica');
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 5000);
      }
    }
  };

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilterOptions(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen">Cargando...</div>;
  }

  // Utility functions for status detection and styling
  const hasLowStock = (insumo) => insumo.stock_actual <= insumo.stock_critico;
  const isExpiringSoon = (insumo) => {
    if (!insumo.fecha_vencimiento) return false;
    const today = new Date();
    const expiryDate = new Date(insumo.fecha_vencimiento);
    const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry > 0; // Próximo a vencer en 30 días
  };
  const isExpired = (insumo) => {
    if (!insumo.fecha_vencimiento) return false;
    const today = new Date();
    const expiryDate = new Date(insumo.fecha_vencimiento);
    return expiryDate < today;
  };
  const getItemStatus = (insumo) => {
    if (isExpired(insumo)) return 'expired';
    if (hasLowStock(insumo)) return 'low_stock';
    if (isExpiringSoon(insumo)) return 'expiring';
    return 'normal';
  };
  const getRowClassName = (item) => {
    const status = getItemStatus(item);
    switch (status) {
      case 'expired':
        return 'bg-gray-100 hover:bg-gray-200 border-l-4 border-gray-500';
      case 'low_stock':
        return 'bg-red-50 hover:bg-red-100 border-l-4 border-red-500';
      case 'expiring':
        return 'bg-yellow-50 hover:bg-yellow-100 border-l-4 border-yellow-500';
      default:
        return 'hover:bg-gray-50';
    }
  };
  const getTextColor = (item) => {
    const status = getItemStatus(item);
    switch (status) {
      case 'expired':
        return 'text-gray-700';
      case 'low_stock':
        return 'text-red-900';
      case 'expiring':
        return 'text-yellow-900';
      default:
        return 'text-gray-900';
    }
  };
  
  // Mantener compatibilidad con código existente
  const isCritical = hasLowStock;

  // Determinar si hay insumos que necesitan compra (críticos o vencidos)
  const hayInsumosParaComprar = insumos.some(isCritical) || insumos.some(isExpired);

  // Definir columnas para la tabla responsiva
  const columns = [
    {
      key: 'nombre',
      label: 'Producto',
      render: (value, item) => (
        <div className={`text-sm font-medium ${isCritical(item) ? 'text-red-900' : 'text-gray-900'}`} title={value}>
          <div className="break-words whitespace-normal">{value}</div>
          {item.descripcion && (
            <div className={`text-xs ${isCritical(item) ? 'text-red-600' : 'text-gray-400'} break-words whitespace-normal`} title={item.descripcion}>
              {item.descripcion}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'stock_info',
      label: 'Stock',
      render: (value, item) => (
        <div className="text-sm">
          <div className={`font-semibold ${isCritical(item) ? 'text-red-600' : 'text-gray-900'}`}>
            {item.stock_actual}
          </div>
          <div className={`text-xs ${isCritical(item) ? 'text-red-500' : 'text-gray-400'}`}>
            / {item.stock_critico} {item.unidad_medida}
          </div>
        </div>
      )
    },
    {
      key: 'valores',
      label: 'Precios',
      render: (value, item) => (
        <div className="text-sm">
          <div className={`text-xs ${isCritical(item) ? 'text-red-600' : 'text-gray-500'}`}>
            Unit: {item.valor_unitario_formato}
          </div>
          <div className={`font-medium ${isCritical(item) ? 'text-red-700' : 'text-green-600'}`}>
            Apl: {item.valor_aplicacion_formato}
          </div>
        </div>
      )
    },
    {
      key: 'estado',
      label: 'Estado',
      render: (value, item) => {
        const status = getItemStatus(item);
        const statusConfig = {
          expired: {
            bg: 'bg-gray-100 text-gray-800',
            text: 'Vencido',
            icon: '⚫',
            animate: false
          },
          low_stock: {
            
            text: 'Stock Faltante',
            icon: '🔴',
            animate: true
          },
          expiring: {
            bg: 'bg-yellow-100 text-yellow-800',
            text: 'Por Vencer',
            icon: '🟡',
            animate: true
          },
          normal: {
            bg: 'bg-primary/10 text-primary-dark',
            text: 'Normal',
            icon: '🟢',
            animate: false
          }
        };
        const config = statusConfig[status];
        return (
          <div className="text-sm">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.animate ? 'animate-pulse' : ''}`}>
              <span className="mr-1">{config.icon}</span>
              {config.text}
            </span>
            {item.fecha_vencimiento && (
              <div className={`text-xs mt-1 ${
                status === 'expired' ? 'text-gray-500' :
                status === 'low_stock' ? 'text-red-500' :
                status === 'expiring' ? 'text-yellow-600' :
                'text-gray-400'
              }`}>
                {status === 'expired' ? 'Venció: ' : 'Vence: '}
                {new Date(item.fecha_vencimiento).toLocaleDateString('es-ES', { 
                  day: '2-digit', 
                  month: '2-digit', 
                  year: '2-digit' 
                })}
              </div>
            )}
          </div>
        );
      }
    }
  ];

  return (
    <div className="p-6">
      {showAlert && (
        <div className={`mb-4 p-4 ${alertMessage.includes('Error') ? 'bg-red-100 border-red-400 text-red-700' : 'bg-green-100 border-green-400 text-green-700'} rounded relative`}>
          <span className="block sm:inline">{alertMessage}</span>
          <button
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
            onClick={() => setShowAlert(false)}
          >
            <span className="sr-only">Cerrar</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Alertas de estado */}
      {(insumos.some(hasLowStock) || insumos.some(isExpiringSoon) || insumos.some(isExpired)) && (
        <div className="mb-6 space-y-3">
          {/* Alerta de stock faltante */}
          {insumos.some(hasLowStock) && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    <strong>🔴 Stock Faltante:</strong> {insumos.filter(hasLowStock).length} insumo(s) requieren reposición inmediata.
                  </p>
                </div>
              </div>
            </div>
          )}
          {/* Alerta de productos por vencer */}
          {insumos.some(isExpiringSoon) && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    <strong>🟡 Por Vencer:</strong> {insumos.filter(isExpiringSoon).length} insumo(s) vencen en los próximos 30 días.
                  </p>
                </div>
              </div>
            </div>
          )}
          {/* Alerta de productos vencidos */}
          {insumos.some(isExpired) && (
            <div className="bg-gray-50 border-l-4 border-gray-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-gray-700">
                    <strong>⚫ Vencidos:</strong> {insumos.filter(isExpired).length} insumo(s) han vencido y deben ser retirados.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 space-y-4 sm:space-y-0">
        <h1 className="text-xl sm:text-2xl font-bold">Inventario</h1>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <button
            onClick={imprimirListaCompras}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Imprimir lista de compras"
            disabled={!hayInsumosParaComprar}
          >
            <PrinterIcon className="h-5 w-5 mr-2" />
            Imprimir Lista de Compras
          </button>
          <AddButton
            onClick={() => {
              setEditingInsumo(null);
              setFormData({
                nombre: '',
                descripcion: '',
                unidad_medida: '',
                stock_actual: '',
                stock_critico: '',
                valor_unitario: '',
                contenido_envase: '',
                valor_aplicacion: '',
                fecha_vencimiento: ''
              });
              setFormError('');
              setShowForm(true);
            }}
          >
            Nuevo Insumo
          </AddButton>
          <div className="relative">
            <Menu as="div" className="relative inline-block text-left">
              <Menu.Button className="inline-flex items-center justify-center p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500">
                <FunnelIcon className="h-5 w-5" aria-hidden="true" />
                <span className="sr-only">Filtrar</span>
                {(filterOptions.soloStockCritico || filterOptions.soloProximosVencer || filterOptions.soloVencidos) && (
                  <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-indigo-400"></span>
                )}
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-50 mt-2 w-72 origin-top-right rounded-md bg-white shadow-xl ring-1 ring-black ring-opacity-20 focus:outline-none border border-gray-200">
                  <div className="py-3 px-4 bg-white rounded-md">
                    <h3 className="text-sm font-medium text-gray-900 mb-3 border-b border-gray-200 pb-2">Filtros</h3>
                    <div className="space-y-4 bg-white">
                      <div className="flex items-center p-2 rounded hover:bg-gray-50">
                        <input
                          type="checkbox"
                          id="soloStockCritico"
                          name="soloStockCritico"
                          checked={filterOptions.soloStockCritico}
                          onChange={handleFilterChange}
                          className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                        />
                        <label htmlFor="soloStockCritico" className="ml-3 text-sm text-gray-700 cursor-pointer">
                          🔴 Solo Stock Faltante
                        </label>
                      </div>
                      
                      <div className="flex items-center p-2 rounded hover:bg-gray-50">
                        <input
                          type="checkbox"
                          id="soloProximosVencer"
                          name="soloProximosVencer"
                          checked={filterOptions.soloProximosVencer}
                          onChange={handleFilterChange}
                          className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                        />
                        <label htmlFor="soloProximosVencer" className="ml-3 text-sm text-gray-700 cursor-pointer">
                          🟡 Solo Por Vencer
                        </label>
                      </div>
                      
                      <div className="flex items-center p-2 rounded hover:bg-gray-50">
                        <input
                          type="checkbox"
                          id="soloVencidos"
                          name="soloVencidos"
                          checked={filterOptions.soloVencidos}
                          onChange={handleFilterChange}
                          className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300 rounded"
                        />
                        <label htmlFor="soloVencidos" className="ml-3 text-sm text-gray-700 cursor-pointer">
                          ⚫ Solo Vencidos
                        </label>
                      </div>
                      
                      <div className="bg-white p-2 rounded">
                        <label htmlFor="ordenarPor" className="block text-sm font-medium text-gray-700 mb-2">
                          Ordenar por
                        </label>
                        <select
                          id="ordenarPor"
                          name="ordenarPor"
                          value={filterOptions.ordenarPor}
                          onChange={handleFilterChange}
                          className="block w-full pl-3 pr-10 py-2 text-sm bg-white border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 rounded-md shadow-sm"
                        >
                          <option value="nombre">Nombre</option>
                          <option value="stock_actual">Stock Actual</option>
                          <option value="stock_critico">Faltante (Crítico - Actual)</option>
                        </select>
                      </div>
                      
                      <div className="bg-white p-2 rounded">
                        <label htmlFor="ordenDireccion" className="block text-sm font-medium text-gray-700 mb-2">
                          Dirección
                        </label>
                        <select
                          id="ordenDireccion"
                          name="ordenDireccion"
                          value={filterOptions.ordenDireccion}
                          onChange={handleFilterChange}
                          className="block w-full pl-3 pr-10 py-2 text-sm bg-white border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 rounded-md shadow-sm"
                        >
                          <option value="asc">Ascendente</option>
                          <option value="desc">Descendente</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>

      {/* Contador de insumos */}
      <div className="mb-4 text-sm text-gray-600">
        Mostrando {insumosFiltered.length} de {insumos.length} insumos
        {(filterOptions.soloStockCritico || filterOptions.soloProximosVencer || filterOptions.soloVencidos) && (
          <span className="ml-2 font-medium">
            (
            {[
              filterOptions.soloStockCritico && <span key="stock" className="text-red-600">Stock faltante</span>,
              filterOptions.soloProximosVencer && <span key="vencer" className="text-yellow-600">Por vencer</span>,
              filterOptions.soloVencidos && <span key="vencidos" className="text-gray-600">Vencidos</span>
            ].filter(Boolean).reduce((prev, curr, index) => 
              index === 0 ? [curr] : [...prev, ', ', curr], []
            )}
            )
          </span>
        )}
      </div>

      <ResponsiveTable
        data={insumosFiltered}
        columns={columns}
        onEdit={handleEdit}
        onDelete={(insumo) => handleDelete(insumo.id)}
        emptyMessage={
          filterOptions.soloStockCritico ? 'No hay insumos con stock faltante' :
          filterOptions.soloProximosVencer ? 'No hay insumos próximos a vencer' :
          filterOptions.soloVencidos ? 'No hay insumos vencidos' :
          'No hay insumos disponibles'
        }
        keyField="id"
        getRowClassName={getRowClassName}
        getTextColor={getTextColor}
      />

      {/* Modal de Formulario */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center" onClick={(e) => e.target === e.currentTarget && setShowForm(false)}>
          <div className="relative mx-4 p-6 border w-full max-w-md sm:max-w-2xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingInsumo ? 'Editar Insumo' : 'Nuevo Insumo'}
              </h3>
              {formError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {formError}
                </div>
              )}
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">Nombre</label>
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    onBlur={(e) => setFormData({ ...formData, nombre: formatInputText(e.target.value) })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">Descripción (opcional)</label>
                  <textarea
                    value={formData.descripcion}
                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                    onBlur={(e) => setFormData({ ...formData, descripcion: formatInputText(e.target.value) })}
                    rows="3"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">Unidad de Medida</label>
                  <input
                    type="text"
                    value={formData.unidad_medida}
                    onChange={(e) => setFormData({ ...formData, unidad_medida: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Stock Actual</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.stock_actual}
                      onChange={(e) => setFormData({ ...formData, stock_actual: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Stock Crítico</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.stock_critico}
                      onChange={(e) => setFormData({ ...formData, stock_critico: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                      required
                    />
                  </div>
                </div>

                {/* Sección de Costos - Grid de 3 columnas */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Costo del Envase ($)</label>
                    <div className="relative mt-1">
                      <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                        $
                      </span>
                      <input
                        type="number"
                        min="0"
                        value={formData.valor_unitario}
                        onChange={(e) => setFormData({ ...formData, valor_unitario: e.target.value })}
                        className="pl-7 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                        placeholder="Ej: 10000"
                        required
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Precio compra de la caja/botella
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Contenido Total</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.contenido_envase}
                      onChange={(e) => setFormData({ ...formData, contenido_envase: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                      placeholder="Ej: 100 (unid/ml)"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Cuántas unidades trae el envase
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Costo x Aplicación</label>
                    <div className="relative mt-1">
                      <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-green-600">
                        $
                      </span>
                      <input
                        type="number"
                        min="0"
                        value={formData.valor_aplicacion}
                        readOnly
                        className="pl-7 block w-full rounded-md border-gray-200 bg-gray-100 text-gray-600 shadow-sm cursor-not-allowed"
                        placeholder="Calculado"
                      />
                    </div>
                    <p className="mt-1 text-xs text-green-600 font-medium">
                      ✓ Calculado automáticamente
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Fecha de Vencimiento (opcional)</label>
                    <input
                      type="date"
                      value={formData.fecha_vencimiento}
                      onChange={(e) => setFormData({ ...formData, fecha_vencimiento: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingInsumo(null);
                      setFormError('');
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-accent text-white rounded-md hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
                  >
                    {editingInsumo ? 'Actualizar' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventarioPage; 